import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import SblChatWidget from "../components/SblChatWidget";
import { Button } from "../components/ui/button";
import { Sparkle, Video, Books, UsersThree, Brain, ArrowRight, GraduationCap } from "@phosphor-icons/react";
import PodPricingTiers from "../components/PodPricingTiers";

const SBL_LOGO = "/sbl-logo.jpeg";

export default function PodLandingPage() {
  const { user } = useAuth();

  const services = [
    {
      icon: Video,
      title: "Webinar Centre",
      blurb: "Live and recorded sessions across Leadership, Career Growth, Tutoring, Money & Sustainability, and Technology.",
      to: "/pod/webinars",
      bg: "from-blue-700 to-indigo-800",
      iconBg: "bg-blue-100 text-blue-800",
      testid: "pod-card-webinars",
    },
    {
      icon: Books,
      title: "Resource Library",
      blurb: "Practical templates, documents, presentations, and spreadsheets for school administration, teaching, leadership and business.",
      to: "/pod/resources",
      bg: "from-amber-600 to-orange-700",
      iconBg: "bg-amber-100 text-amber-800",
      testid: "pod-card-resources",
    },
    {
      icon: UsersThree,
      title: "Virtual Roundtables",
      blurb: "Auto-matched professional communities — Principals, Teachers, Proprietors, ICT, Counsellors — share, network, learn together.",
      to: "/pod/roundtable",
      bg: "from-emerald-700 to-teal-800",
      iconBg: "bg-emerald-100 text-emerald-800",
      testid: "pod-card-roundtable",
    },
    {
      icon: Brain,
      title: "AI Template Generator",
      blurb: "Generate customised PDF, Word, Excel or PowerPoint templates with AI — lesson plans, reports, budgets and more.",
      to: "/pod/ai-templates",
      bg: "from-slate-800 to-slate-950",
      iconBg: "bg-slate-100 text-slate-900",
      testid: "pod-card-ai",
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="pod-landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={SBL_LOGO} alt="SBL" className="w-9 h-9 object-contain rounded-sm" />
            <span className="text-base sm:text-lg font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>School Builder Lab Africa</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm text-slate-600">
            <Link to="/" className="hover:text-slate-950">Home</Link>
            {user ? (
              <Link to="/dashboard"><Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none">Dashboard</Button></Link>
            ) : (
              <Link to="/pod/signup"><Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="pod-signup-cta">Join POD</Button></Link>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <Sparkle weight="fill" size={18} className="text-amber-300" />
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">SBL Leadership POD</p>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Leadership Beyond the Classroom. Building Sustainable Schools Together.
            </h1>
            <p className="text-base sm:text-lg text-blue-100 mb-8 leading-relaxed">
              The SBL Leadership Pod is a professional growth community for educators, school leaders, administrators, online tutors, education entrepreneurs and aspiring professionals who want continuous learning, leadership development, visibility, networking and meaningful collaboration — without committing to the full SBL Fellowship.
            </p>
            <div className="flex gap-3 flex-wrap">
              <Link to="/pod/signup">
                <Button className="bg-amber-500 text-slate-950 hover:bg-amber-400 rounded-none gap-2 px-6 py-6 text-base" data-testid="hero-pod-join">
                  Join the POD <ArrowRight weight="bold" size={16} />
                </Button>
              </Link>
              <Link to="/pod/webinars">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-slate-950 rounded-none px-6 py-6 text-base bg-transparent">
                  Browse Webinars
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-16 sm:py-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-950 text-white p-8 sm:p-10">
              <p className="text-xs uppercase tracking-[0.25em] text-amber-300 font-bold mb-3">Our Mission</p>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>What we're building</h2>
              <p className="text-base text-white/85 leading-relaxed">
                To build a strong and supportive community of educators and school leaders equipped with practical knowledge, leadership capacity, digital skills, career growth opportunities, operational systems, and collaborative support needed to create sustainable schools and meaningful educational impact.
              </p>
            </div>
            <div className="bg-blue-900 text-white p-8 sm:p-10">
              <p className="text-xs uppercase tracking-[0.25em] text-amber-300 font-bold mb-3">More Than Membership</p>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>It's an ecosystem</h2>
              <p className="text-base text-white/85 leading-relaxed">
                For educators who want to grow professionally, increase their impact, improve their earning potential, and build sustainable careers in education. Monthly webinars, a growing resource library, quarterly virtual roundtables and visibility opportunities — all in one place.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What members get */}
      <section className="py-16 sm:py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-slate-500 font-bold mb-2">What Members Get</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-950 mb-12" style={{ fontFamily: "'Outfit', sans-serif" }}>Four pillars of growth</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { num: "01", title: "Monthly Webinars", desc: "Practical leadership, career growth, school systems, digital opportunities and sustainable success in education." },
              { num: "02", title: "Resource Library", desc: "Growing library of templates, checklists, lesson plans, CV samples, pricing tools, policy docs and more." },
              { num: "03", title: "Quarterly Roundtables", desc: "Peer learning, collaboration, accountability and real-life problem-solving conversations every quarter." },
              { num: "04", title: "Visibility Opportunities", desc: "Build professional presence, develop thought leadership, share your impact and unlock speaking opportunities." },
            ].map(p => (
              <div key={p.num} className="bg-white border border-slate-200 p-6 hover:border-blue-700 hover:shadow-md transition-all">
                <p className="text-xs font-bold text-amber-600 mb-2">{p.num}</p>
                <h3 className="text-lg font-bold mb-2 text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Webinar topic categories */}
      <section className="py-16 sm:py-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-slate-500 font-bold mb-2">Webinar Topics</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-950 mb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>5 themes · 40+ topics every year</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: "Leadership & School Growth", items: ["School leadership & administration", "Sustainable school systems", "Staff management & culture", "Parent communication", "Admissions & enrollment strategy"] },
              { title: "Career Growth & Professional Development", items: ["Career paths beyond teaching", "Transitioning into leadership", "Professional brand building", "CV & LinkedIn optimisation", "Public speaking & visibility"] },
              { title: "Online Tutoring & Digital Opportunities", items: ["Becoming a successful online tutor", "Creating & selling resources", "Building a tutoring business", "Canva & AI tools for teaching", "Monetising educational knowledge"] },
              { title: "Money, Sustainability & Income Growth", items: ["Financial literacy for educators", "Multiple income streams", "Pricing your skills confidently", "Side hustles for teachers", "Education consulting"] },
              { title: "Technology & Innovation", items: ["AI tools for educators", "Digital systems for schools", "EdTech opportunities in Africa", "Productivity tools", "Efficient workflows"] },
            ].map(cat => (
              <div key={cat.title} className="bg-slate-50 border border-slate-200 p-5">
                <h3 className="text-base font-bold mb-3 text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>{cat.title}</h3>
                <ul className="space-y-1 text-xs text-slate-600">
                  {cat.items.map(i => <li key={i} className="flex items-start gap-1.5"><span className="text-amber-600 mt-0.5">•</span> {i}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who is it for */}
      <section className="py-16 sm:py-20 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-amber-300 font-bold mb-2">Who Is The POD For?</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>Built for these education professionals</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {["Teachers", "School administrators", "Operations staff", "Online tutors", "Aspiring school leaders", "School owners", "Education professionals", "Young graduates", "EdTech enthusiasts", "Education entrepreneurs", "Student teachers", "Career explorers"].map(role => (
              <div key={role} className="border border-white/15 bg-white/5 px-4 py-3 text-sm">{role}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <PodPricingTiers />
        </div>
      </section>

      {/* Services */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-slate-500 font-bold mb-2">What's inside</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-950 mb-12" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Four powerful service areas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map(s => {
              const Icon = s.icon;
              return (
                <Link key={s.title} to={s.to} className="group block" data-testid={s.testid}>
                  <div className="bg-white border border-slate-200 hover:border-blue-700 hover:shadow-xl transition-all p-6 sm:p-8 h-full">
                    <div className={`w-12 h-12 ${s.iconBg} flex items-center justify-center mb-4`}>
                      <Icon size={24} weight="fill" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-950 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-4">{s.blurb}</p>
                    <span className={`inline-flex items-center gap-2 text-sm font-semibold bg-gradient-to-r ${s.bg} bg-clip-text text-transparent group-hover:gap-3 transition-all`}>
                      Open {s.title} <ArrowRight size={14} weight="bold" className="text-slate-500" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <GraduationCap weight="fill" size={48} className="mx-auto text-blue-800 mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Ready to join the POD?</h2>
          <p className="text-base text-slate-600 mb-6">Tell us about your role and goals — we'll match you to the right roundtable and curate webinars, resources, and networking opportunities just for you.</p>
          <Link to="/pod/signup">
            <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none px-8 py-6 text-base gap-2">
              Join POD Now <ArrowRight weight="bold" size={16} />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-xs">
          © {new Date().getFullYear()} School Builder Lab Africa — SBL Leadership POD. All rights reserved.
        </div>
      </footer>

      <SblChatWidget />
    </div>
  );
}
